/**
 * <ticker-bar>
 * - Duplicates its list once so the CSS translateX(-50%) loop is seamless.
 * - Slides in after the page has been scrolled past data-threshold (unless
 *   data-show-on-scroll="false"), and sets html.ticker-visible so sticky
 *   elements can offset themselves with --ticker-offset.
 */
if (!customElements.get('ticker-bar')) {
  customElements.define(
    'ticker-bar',
    class TickerBar extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.list = this.querySelector('[data-list]');
        this.showOnScroll = this.dataset.showOnScroll !== 'false';
        this.threshold = parseInt(this.dataset.threshold, 10) || 80;
        this.fill();
        this.onScroll = this.onScroll.bind(this);
        if (this.showOnScroll) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        } else {
          this.setVisible(true);
        }
        this.resizeObserver = new ResizeObserver(() => this.fill());
        this.resizeObserver.observe(document.documentElement);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        document.documentElement.classList.remove('ticker-visible');
      }

      /* Repeat the list until one copy is at least as wide as the viewport, then clone the whole set once. */
      fill() {
        if (!this.track || !this.list) return;
        this.track.querySelectorAll('[data-clone]').forEach((el) => el.remove());
        const viewport = this.clientWidth || window.innerWidth;
        let width = this.list.getBoundingClientRect().width;
        if (!width) return;
        const copies = [];
        while (width < viewport) {
          const copy = this.list.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.setAttribute('data-clone', '');
          copies.push(copy);
          width += this.list.getBoundingClientRect().width;
          if (copies.length > 20) break;
        }
        copies.forEach((c) => this.track.appendChild(c));
        // Second half for the seamless loop
        const set = Array.from(this.track.children).map((el) => {
          const c = el.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('data-clone', '');
          return c;
        });
        set.forEach((c) => this.track.appendChild(c));
      }

      onScroll() {
        this.setVisible((window.scrollY || 0) > this.threshold);
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        document.documentElement.classList.toggle('ticker-visible', visible);
      }
    }
  );
}
